import type { ReactNode } from 'react';
import type { SupportAccessGrant } from '@traceio-org/platform-client';
import { DateTimeText, ScrollTable, StatusBadge } from './records';
import { STAFF_ROLE_LABELS } from '../format';
import { CLOCK_SKEW_MS } from '../pages/organization/scope';

export type GrantStatus = 'active' | 'scheduled' | 'ended' | 'revoked';

/**
 * A grant's status at `now`: revoked, ended (expired), scheduled (not started) or active. A grant that starts
 * within the clock skew platform-api and the browser may have counts as started (isGrantActive).
 */
export function grantStatus(grant: SupportAccessGrant, now: number): GrantStatus {
  if (grant.revokedAt !== null) return 'revoked';
  if (Date.parse(grant.expiresAt) <= now) return 'ended';
  if (Date.parse(grant.startsAt) > now + CLOCK_SKEW_MS) return 'scheduled';
  return 'active';
}

export const SCOPE_LABELS: Readonly<Record<SupportAccessGrant['scope'], string>> = {
  read: 'Read only',
  write: 'Read and write',
};

/**
 * Support access grants as a table: who (you, or another staff member by role: grants carry no name), the
 * scope, reason and ticket, when it started and ended, and its status now. `first` adds a leading column
 * (the organization, on the staff member's own page).
 */
export function GrantsTable({
  grants,
  caption,
  staffUserId,
  now,
  busy = false,
  first,
  onEnd,
}: {
  grants: readonly SupportAccessGrant[];
  caption: string;
  staffUserId: string;
  now: number;
  busy?: boolean;
  first?: { label: string; render: (grant: SupportAccessGrant) => ReactNode };
  /** Ends a grant in force; given only to staff who may end some grant here. */
  onEnd?: { canEnd: (grant: SupportAccessGrant) => boolean; end: (grant: SupportAccessGrant) => void };
}) {
  return (
    <ScrollTable caption={caption} busy={busy} className="admin-table admin-table--grants">
      <thead>
        <tr>
          {first ? <th scope="col">{first.label}</th> : null}
          <th scope="col">Staff</th>
          <th scope="col">Scope</th>
          <th scope="col">Reason</th>
          <th scope="col">Ticket</th>
          <th scope="col">Started</th>
          <th scope="col">Ends or ended</th>
          <th scope="col">Status</th>
          {onEnd ? <th scope="col">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {grants.map((grant) => {
          const status = grantStatus(grant, now);
          const ended = grant.revokedAt ?? grant.expiresAt;
          return (
            <tr key={grant.id}>
              {first ? <td>{first.render(grant)}</td> : null}
              <th scope="row">
                {grant.staffUserId === staffUserId ? 'You' : 'Another staff member'}
                <span className="admin-cell-secondary">{STAFF_ROLE_LABELS[grant.staffRole]}</span>
              </th>
              <td>{SCOPE_LABELS[grant.scope]}</td>
              <td className="admin-cell-wrap">{grant.reason}</td>
              <td>
                {grant.ticketRef ? <code>{grant.ticketRef}</code> : <span className="admin-muted">None</span>}
              </td>
              <td>
                <DateTimeText iso={grant.startsAt} />
              </td>
              <td>
                <DateTimeText iso={ended} />
              </td>
              <td>
                <StatusBadge status={status} />
              </td>
              {onEnd ? (
                <td>
                  {status === 'active' && onEnd.canEnd(grant) ? (
                    <button
                      type="button"
                      className="admin-action"
                      aria-label={
                        grant.staffUserId === staffUserId
                          ? 'End your support access'
                          : `End the support access of another staff member (${STAFF_ROLE_LABELS[grant.staffRole]})`
                      }
                      onClick={() => onEnd.end(grant)}
                    >
                      End
                    </button>
                  ) : (
                    <span className="admin-muted">None</span>
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </ScrollTable>
  );
}
